import React from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/compat/router';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useKioskLiveness } from 'src/hooks/use-kiosk-liveness';

import { Header } from 'src/dispensary/core-menu/components/header';
import { ConciergeFab } from 'src/dispensary/core-menu/components/concierge-fab';
import { useIsConciergeEnabled } from 'src/concierge/use-is-concierge-enabled';
import { KioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout';
import CoreMenuFooter from 'dispensary/core-menu/components/core-menu-footer';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import { useOrderTypeTakeoverPrompt } from 'src/hooks/use-order-type-takeover-prompt';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';

import { LayoutWrapper } from './layout-wrapper';

type CoreMenuLayoutProps = {
  children: React.ReactNode;
};

export const CoreMenuLayout = observer(({ children }: CoreMenuLayoutProps): JSX.Element | null => {
  const { dispensary, loading, error } = useDispensary();
  const router = useRouter();
  const isKioskProEnabled = useKioskPro();
  const UI = useUI();
  const { onCollectionsPage, isKiosk } = UI;
  // Concierge FAB: storefront only. Per product decision the fixed-position
  // floating button never shows on embedded (iframe) menus, flag on or not,
  // because a fixed element does not behave reliably inside an auto-resized,
  // host-page-controlled iframe. The embedded + small-viewport case is covered
  // instead by ConciergeEntryBanner (see navigation.tsx), the full-width
  // non-floating entry point added for ECOMM-12462.
  const isConciergeFabEnabled = useIsConciergeEnabled(dispensary, UI, { allowEmbedded: false });

  // Pass the dispensary id already fetched above so the liveness ping doesn't run a second query.
  // ECOMM-12178: the PIN is optional -- this only reports liveness for an already-claimed
  // device, it never blocks the menu (see `/kiosks/[cName]/setup` for claiming a PIN).
  useKioskLiveness(dispensary?.id ?? null);

  useOrderTypeTakeoverPrompt();

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean expression */
  const hideCategories =
    isKioskProEnabled || !(router?.route.includes('products') || router?.route.includes('brands') || onCollectionsPage);
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
  const hideFooter = isKioskProEnabled && isKiosk;

  if (isKioskProEnabled && isKiosk && dispensary) {
    return (
      <KioskProLayout dispensary={dispensary} hideCategories={hideCategories} hideFooter={hideFooter}>
        {children}
      </KioskProLayout>
    );
  }

  if (error) {
    console.error('Error fetching dispensary', error);
  }

  if (!loading && !dispensary && !error) {
    console.error('Dispensary not found', error);
    return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
  }

  return (
    <LayoutWrapper>
      <Header dispensary={dispensary} />

      {isConciergeFabEnabled && <ConciergeFab cName={dispensary?.cName ?? null} />}

      {children}

      <CoreMenuFooter hideCategories={hideCategories} />
    </LayoutWrapper>
  );
});
